import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import type { AiMood } from './ai-mood.model';
import { useAiTheme } from './ai-theme.storage';
import { ThemeHat, ThemeWeather } from './AiThemeCostumes';

/**
 * AI characters: flat, simple helpers that show what an AI step is doing,
 * first used on the FFU import cards. Every one takes
 * a mood, and while it talks, the mood it talks with. All of them share one
 * flat eye: a dark shape that eases between a dot, an oval, a squint, a ^
 * crescent and a drooping lid, so expressions blend instead of switching.
 */

export type AiCharacterProps = {
  mood: AiMood;
  talkUntil?: number;
  talkMood?: AiMood;
  /** A point to look at, in client coordinates (the pointer). */
  lookAt?: { x: number; y: number } | null;
};

export type AiCharacterComponent = React.FC<AiCharacterProps>;

const BOX = 111;
const CX = BOX / 2;
const CY = BOX / 2 - 2;
const INK = 'rgba(0,0,0,0.8)';
const SHADOW = 'rgba(0,0,0,0.07)';

type Frame = {
  t: number;
  dt: number;
  now: number;
  talking: boolean;
  mood: AiMood;
  /** Direction to {@link AiCharacterProps.lookAt}, -1…1 each way. */
  gaze: [number, number] | null;
};

/** A requestAnimationFrame loop that sees the latest props. */
const useCharacterLoop = (
  props: AiCharacterProps,
  stage: React.RefObject<HTMLDivElement | null>,
  draw: (frame: Frame) => void,
) => {
  const latest = useRef(props);
  latest.current = props;
  const drawRef = useRef(draw);
  drawRef.current = draw;
  useEffect(() => {
    let id = 0;
    const start = performance.now();
    let last = start;
    const step = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const { mood, talkUntil = 0, talkMood, lookAt } = latest.current;
      const talking = Date.now() < talkUntil;
      let gaze: [number, number] | null = null;
      const box = stage.current?.getBoundingClientRect();
      if (lookAt && box) {
        const clamp = (v: number) => Math.max(-1, Math.min(1, v));
        gaze = [
          clamp((lookAt.x - (box.left + box.width / 2)) / 90),
          clamp((lookAt.y - (box.top + box.height / 2)) / 60),
        ];
      }
      drawRef.current({
        t: (now - start) / 1000,
        dt,
        now,
        talking,
        mood: talking ? (talkMood ?? mood) : mood,
        gaze,
      });
      id = requestAnimationFrame(step);
    };
    id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
  }, [stage]);
};

/** Eases every number in `cur` toward `target`. */
const easeToward = <T extends Record<string, number>>(
  cur: T,
  target: T,
  dt: number,
  rate = 6,
) => {
  const k = 1 - Math.exp(-dt * rate);
  for (const key of Object.keys(cur) as (keyof T)[]) {
    (cur[key] as number) +=
      ((target[key] as number) - (cur[key] as number)) * k;
  }
};

/** A damped spring toward 0, for squash and stretch. */
const createSpring = (stiffness = 180, damping = 12) => {
  let x = 0;
  let v = 0;
  return {
    kick: (impulse: number) => {
      v += impulse;
    },
    step: (dt: number) => {
      v += (-stiffness * x - damping * v) * dt;
      x += v * dt;
      return x;
    },
  };
};

/** 0 while the eyes are open, 1 at the middle of a blink. */
const createBlinker = () => {
  let next = performance.now() + 1800 + Math.random() * 2000;
  let start = -1;
  return (now: number): number => {
    if (start < 0 && now > next) start = now;
    if (start < 0) return 0;
    const p = (now - start) / 150;
    if (p >= 1) {
      start = -1;
      next = now + 2400 + Math.random() * 3200;
      return 0;
    }
    return 1 - Math.abs(1 - 2 * p);
  };
};

/** Closed smooth path through points (Catmull-Rom as cubic Béziers). */
const smoothPath = (points: readonly [number, number][]): string => {
  const n = points.length;
  const at = (i: number) => points[((i % n) + n) % n]!;
  let d = `M ${at(0)[0].toFixed(2)} ${at(0)[1].toFixed(2)}`;
  for (let i = 0; i < n; i++) {
    const [x0, y0] = at(i - 1);
    const [x1, y1] = at(i);
    const [x2, y2] = at(i + 1);
    const [x3, y3] = at(i + 2);
    d +=
      ` C ${(x1 + (x2 - x0) / 6).toFixed(2)} ${(y1 + (y2 - y0) / 6).toFixed(2)}` +
      ` ${(x2 - (x3 - x1) / 6).toFixed(2)} ${(y2 - (y3 - y1) / 6).toFixed(2)}` +
      ` ${x2.toFixed(2)} ${y2.toFixed(2)}`;
  }
  return `${d} Z`;
};

const set = (el: Element | null, attrs: Record<string, string | number>) => {
  if (!el) return;
  for (const [key, value] of Object.entries(attrs)) {
    el.setAttribute(key, typeof value === 'number' ? value.toFixed(2) : value);
  }
};

const Stage = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  ({ children }, ref) => {
    const theme = useAiTheme();
    return (
      <Box
        ref={ref}
        sx={{
          width: BOX,
          height: BOX,
          flexShrink: 0,
          position: 'relative',
          top: 4,
        }}
      >
        <svg width={BOX} height={BOX} viewBox={`0 0 ${BOX} ${BOX}`}>
          <ThemeWeather weather={theme.weather} />
          {children}
        </svg>
      </Box>
    );
  },
);
Stage.displayName = 'AiCharacterStage';

// ---------------------------------------------------------------------------
// The shared flat eye.
// ---------------------------------------------------------------------------

type Eye = {
  /** Half width and half height. */
  w: number;
  h: number;
  /** Top lid curve: 1.33 is round, 0 is a flat lid. */
  top: number;
  /** Bottom curve: 1.33 is round, negative pushes it up into a ^ crescent. */
  bottom: number;
  /** Degrees; positive lifts the inner corners (sad). */
  tilt: number;
  /** Where the eyes look, px. */
  lookX: number;
  lookY: number;
  /** Left eye bigger, right smaller (a curious look). */
  asym: number;
  /** Brow lift for helpers that draw brows, px. */
  brow: number;
};

const wanderX = (t: number, speed = 0.5) => Math.sin(t * speed) * 0.8;

const EYES: Record<AiMood, (t: number) => Eye> = {
  idle: (t) => ({
    w: 4,
    h: 5,
    top: 1.33,
    bottom: 1.33,
    tilt: 0,
    lookX: 3 * wanderX(t),
    lookY: 0,
    asym: 0,
    brow: 0,
  }),
  eager: () => ({
    w: 4.6,
    h: 6.4,
    top: 1.33,
    bottom: 1.33,
    tilt: 0,
    lookX: 0,
    lookY: -1.5,
    asym: 0,
    brow: 3,
  }),
  curious: (t) => ({
    w: 4.2,
    h: 5.6,
    top: 1.33,
    bottom: 1.33,
    tilt: -4,
    lookX: 3 * Math.sin(t * 0.9),
    lookY: -0.5,
    asym: 0.18,
    brow: 2,
  }),
  scanning: (t) => ({
    w: 4.6,
    h: 3,
    top: 1,
    bottom: 1.2,
    tilt: 0,
    lookX: 3.5 * Math.sin(t * 2.4),
    lookY: 1,
    asym: 0,
    brow: -1,
  }),
  // Focused, looking down at the blocks it stacks.
  building: () => ({
    w: 4.4,
    h: 4,
    top: 1,
    bottom: 1.33,
    tilt: -3,
    lookX: 3,
    lookY: 2,
    asym: 0,
    brow: 0,
  }),
  thinking: () => ({
    w: 4.2,
    h: 4.4,
    top: 0.25,
    bottom: 1.33,
    tilt: -8,
    lookX: 3,
    lookY: -3,
    asym: -0.1,
    brow: 1,
  }),
  excited: () => ({
    w: 5,
    h: 6.8,
    top: 1.33,
    bottom: 1.33,
    tilt: 0,
    lookX: 0,
    lookY: -1,
    asym: 0,
    brow: 3.5,
  }),
  happy: () => ({
    w: 4.8,
    h: 4.6,
    top: 1.45,
    bottom: -0.85,
    tilt: 0,
    lookX: 0,
    lookY: 0,
    asym: 0,
    brow: 1.5,
  }),
  proud: () => ({
    w: 5,
    h: 3.8,
    top: 1.3,
    bottom: -0.55,
    tilt: 0,
    lookX: 0,
    lookY: -2,
    asym: 0,
    brow: 2,
  }),
  // Inner corners up, a heavy flat lid, looking down: sad, not angry.
  sad: () => ({
    w: 4.2,
    h: 4.2,
    top: 0.1,
    bottom: 1.4,
    tilt: 14,
    lookX: 0,
    lookY: 3,
    asym: 0,
    brow: 2,
  }),
  // Closed: a flat lid over a curve, the eyes of a sleeper.
  sleeping: () => ({
    w: 4.6,
    h: 1.6,
    top: 0.1,
    bottom: 1.33,
    tilt: 0,
    lookX: 0,
    lookY: 1.5,
    asym: 0,
    brow: -1,
  }),
};

/** One flat eye as a closed path around (cx, cy). */
const eyePath = (
  cx: number,
  cy: number,
  w: number,
  h: number,
  top: number,
  bottom: number,
): string => {
  const l = (cx - w).toFixed(2);
  const r = (cx + w).toFixed(2);
  const y = cy.toFixed(2);
  const yt = (cy - h * top).toFixed(2);
  const yb = (cy + h * bottom).toFixed(2);
  return `M ${l} ${y} C ${l} ${yt} ${r} ${yt} ${r} ${y} C ${r} ${yb} ${l} ${yb} ${l} ${y} Z`;
};

/** Draws a pair of eyes into two path elements. */
const drawEyes = (
  els: (SVGPathElement | null)[],
  eye: Eye,
  centre: [number, number],
  spacing: number,
  blink: number,
  scale = 1,
) => {
  [-1, 1].forEach((side, index) => {
    const size = scale * (1 - side * eye.asym);
    const ex = centre[0] + side * spacing + eye.lookX;
    const ey = centre[1] + eye.lookY;
    const h = Math.max(0.5, eye.h * size * (1 - 0.88 * blink));
    set(els[index] ?? null, {
      d: eyePath(ex, ey, eye.w * size, h, eye.top, eye.bottom),
      transform: `rotate(${(side * eye.tilt).toFixed(2)} ${ex.toFixed(2)} ${ey.toFixed(2)})`,
    });
  });
};

const EyePair: React.FC<{
  refs: React.MutableRefObject<(SVGPathElement | null)[]>;
}> = ({ refs }) => (
  <>
    {[0, 1].map((index) => (
      <path
        key={index}
        ref={(el) => {
          refs.current[index] = el;
        }}
        fill={INK}
      />
    ))}
  </>
);

/** The mood's eyes, looking toward the pointer when there is one. */
const gazeEye = (eye: Eye, gaze: [number, number] | null): Eye =>
  gaze === null ? eye : { ...eye, lookX: gaze[0] * 4.5, lookY: gaze[1] * 3.5 };

/** Talking rhythm: 0…1 syllables. */
const syllable = (t: number) => Math.abs(Math.sin(t * 11));

/**
 * Verbs (scanning, thinking) run a loader-like motion that keeps going while
 * the pipeline works. Adjectives get body language, no loader.
 */
type Loader = { scan: number; think: number };
const LOADERS: Record<AiMood, Loader> = {
  idle: { scan: 0, think: 0 },
  eager: { scan: 0, think: 0 },
  curious: { scan: 0, think: 0 },
  scanning: { scan: 1, think: 0 },
  thinking: { scan: 0, think: 1 },
  building: { scan: 0, think: 0 },
  excited: { scan: 0, think: 0 },
  happy: { scan: 0, think: 0 },
  proud: { scan: 0, think: 0 },
  sad: { scan: 0, think: 0 },
  sleeping: { scan: 0, think: 0 },
};

/** A pulse that runs along a row: 1 at the head, 0 elsewhere. */
const travelling = (t: number, i: number, speed = 5, spacing = 1.1) =>
  Math.pow(Math.max(0, Math.cos(t * speed - i * spacing)), 6);

/** Sad: how much the body slumps into an uneven lump of dough. */
const LUMP: Partial<Record<AiMood, number>> = { sad: 1 };

/** Uneven bumps around an outline, the same every frame: a lump, not an ellipse. */
const lumpiness = (angle: number) =>
  0.09 * Math.sin(2 * angle + 0.7) +
  0.06 * Math.sin(3 * angle + 2.3) +
  0.035 * Math.sin(5 * angle + 1.1);

/**
 * Now and then a short shiver, like a sob: a fast sideways tremble for half a
 * second every few seconds. Returns px, scaled by the lump weight.
 */
const createShiver = () => {
  let next = performance.now() + 1500 + Math.random() * 2000;
  let start = -1;
  return (now: number, weight: number): number => {
    if (weight < 0.05) return 0;
    if (start < 0 && now > next) start = now;
    if (start < 0) return 0;
    const p = (now - start) / 450;
    if (p >= 1) {
      start = -1;
      next = now + 2200 + Math.random() * 2600;
      return 0;
    }
    return (
      weight * 1.5 * Math.sin(p * Math.PI) * Math.sin((now - start) * 0.07)
    );
  };
};

/** Proud: small four-point stars twinkle around the head. */
const SPARKLE_GOLD = '#EDA239';
/** Offset from the head, size and phase of each star. */
const SPARKLES: [number, number, number, number][] = [
  [-28, -17, 6.5, 0],
  [26, -23, 5, 2.1],
  [32, 2, 4.5, 4.2],
];

/**
 * Props that come and go with a mood: sparkles (proud), the hard hat and the
 * growing stack of blocks (building), and sheets flying past and reading
 * glasses (scanning).
 */
type Extras = {
  sparkle: number;
  build: number;
  papers: number;
  wonder: number;
  sleep: number;
};
const extrasOf = (mood: AiMood): Extras => ({
  wonder: mood === 'curious' ? 1 : 0,
  sparkle: mood === 'proud' ? 1 : 0,
  build: mood === 'building' ? 1 : 0,
  papers: mood === 'scanning' ? 1 : 0,
  sleep: mood === 'sleeping' ? 1 : 0,
});
const NO_EXTRAS: Extras = {
  sparkle: 0,
  build: 0,
  papers: 0,
  wonder: 0,
  sleep: 0,
};

/** Curious: small question marks, [dx, dy, size, phase], around the face. */
const QUESTIONS: [number, number, number, number][] = [
  [-27, -19, 9, 0],
  [25, -24, 11, 1.3],
  [31, -4, 8, 2.6],
];
/** Seconds for one question mark to pop up, drift and fade. */
const QUESTION_CYCLE = 3.9;

/** Each question mark pops in, drifts up slowly and fades, one after another. */
const drawQuestions = (
  els: (SVGTextElement | null)[],
  cx: number,
  cy: number,
  t: number,
  amount: number,
) => {
  QUESTIONS.forEach(([dx, dy, size, phase], i) => {
    const p = ((t + phase) % QUESTION_CYCLE) / QUESTION_CYCLE;
    // Pop: overshoot to full size in the first tenth, then hold.
    const pop = p < 0.1 ? Math.sin((p / 0.1) * Math.PI * 0.75) / 0.71 : 1;
    const fade = p > 0.7 ? (1 - p) / 0.3 : 1;
    const x = cx + dx;
    const y = cy + dy - 6 * p;
    set(els[i] ?? null, {
      x,
      y,
      'font-size': size,
      opacity: amount * fade,
      transform: `translate(${x} ${y}) scale(${pop * amount}) rotate(${-10 + 20 * Math.sin(t * 0.8 + phase)}) translate(${-x} ${-y})`,
    });
  });
};

/** Sleeping: z's rise from the head one after another, [size, phase]. */
const SNORES: [number, number][] = [
  [8, 0],
  [10.5, 1.1],
  [13, 2.2],
];
/** Seconds for one z to rise and fade. */
const SNORE_CYCLE = 3.3;

/** Each z drifts up and to the right, growing, and fades out at the top. */
const drawSnores = (
  els: (SVGTextElement | null)[],
  cx: number,
  cy: number,
  t: number,
  amount: number,
) => {
  SNORES.forEach(([size, phase], i) => {
    const p = ((t + phase) % SNORE_CYCLE) / SNORE_CYCLE;
    const fadeIn = Math.min(1, p / 0.15);
    const fadeOut = p > 0.65 ? (1 - p) / 0.35 : 1;
    const x = cx + 14 + 18 * p + 2.5 * Math.sin(t * 1.6 + i);
    const y = cy - 16 - 26 * p;
    set(els[i] ?? null, {
      x,
      y,
      'font-size': size * (0.7 + 0.3 * p),
      opacity: amount * fadeIn * fadeOut,
      transform: `rotate(${-12 + 8 * Math.sin(t + phase)} ${x} ${y})`,
    });
  });
};

/** Where a helper draws its eyes, as passed to {@link drawEyes}. */
type EyePlace = {
  eye: Eye;
  centre: [number, number];
  spacing: number;
  scale: number;
};

/**
 * Scanning: square reading glasses over the eyes. They follow the eyes as
 * they sweep, and drop onto the nose as `amount` eases in.
 */
const drawGlasses = (
  el: SVGPathElement | null,
  place: EyePlace | undefined,
  amount: number,
) => {
  if (place === undefined || amount < 0.01) {
    set(el, { opacity: 0 });
    return;
  }
  const { eye, centre, spacing, scale } = place;
  const rx = Math.min(spacing - 1.5, 4.6 * scale + 3);
  const ry = Math.min(rx, 3 * scale + 3.5);
  const cx = centre[0] + eye.lookX;
  const cy = centre[1] + eye.lookY - (1 - amount) * 8;
  // Square frames with softly rounded corners.
  const k = Math.min(rx, ry) * 0.35;
  const ring = (x: number) => {
    const l = x - rx;
    const r = x + rx;
    const t = cy - ry;
    const b = cy + ry;
    return [
      `M ${(l + k).toFixed(2)} ${t.toFixed(2)}`,
      `H ${(r - k).toFixed(2)} Q ${r.toFixed(2)} ${t.toFixed(2)} ${r.toFixed(2)} ${(t + k).toFixed(2)}`,
      `V ${(b - k).toFixed(2)} Q ${r.toFixed(2)} ${b.toFixed(2)} ${(r - k).toFixed(2)} ${b.toFixed(2)}`,
      `H ${(l + k).toFixed(2)} Q ${l.toFixed(2)} ${b.toFixed(2)} ${l.toFixed(2)} ${(b - k).toFixed(2)}`,
      `V ${(t + k).toFixed(2)} Q ${l.toFixed(2)} ${t.toFixed(2)} ${(l + k).toFixed(2)} ${t.toFixed(2)} Z`,
    ].join(' ');
  };
  const left = cx - spacing;
  const right = cx + spacing;
  set(el, {
    d:
      `${ring(left)} ${ring(right)}` +
      ` M ${(left + rx).toFixed(2)} ${(cy - 1).toFixed(2)}` +
      ` Q ${cx.toFixed(2)} ${(cy - 3).toFixed(2)} ${(right - rx).toFixed(2)} ${(cy - 1).toFixed(2)}`,
    opacity: amount,
    'stroke-width': Math.max(1.2, scale * 1.3),
  });
};

/** Twinkles the stars around (cx, cy); `amount` fades them in and out. */
const drawSparkles = (
  els: (SVGPathElement | null)[],
  cx: number,
  cy: number,
  t: number,
  amount: number,
) => {
  SPARKLES.forEach(([dx, dy, size, phase], i) => {
    // Each star swells and shrinks on its own beat, never all at once.
    const beat = Math.max(0, Math.sin(t * 3 + phase));
    const r = size * amount * (0.35 + 0.65 * beat);
    const x = cx + dx;
    const y = cy + dy;
    const k = r * 0.22;
    set(els[i] ?? null, {
      d:
        `M ${x} ${y - r} Q ${x + k} ${y - k} ${x + r} ${y}` +
        ` Q ${x + k} ${y + k} ${x} ${y + r}` +
        ` Q ${x - k} ${y + k} ${x - r} ${y}` +
        ` Q ${x - k} ${y - k} ${x} ${y - r} Z`,
      opacity: amount * (0.4 + 0.6 * beat),
      transform: `rotate(${(t * 40 + phase * 30) % 360} ${x} ${y})`,
    });
  });
};

const Sparkles: React.FC<{
  refs: React.RefObject<(SVGPathElement | null)[]>;
}> = ({ refs }) => (
  <>
    {SPARKLES.map((_, i) => (
      <path
        key={`sparkle${i}`}
        ref={(el) => {
          refs.current[i] = el;
        }}
        fill={SPARKLE_GOLD}
        opacity={0}
      />
    ))}
  </>
);

const HARD_HAT = '#EDA239';
const BLOCK = '#C8ACB0';
const BLOCK_W = 10;
const BLOCK_H = 7;
/** Seconds to stack three blocks before the stack starts over. */
const BUILD_CYCLE = 3.6;

/**
 * Building: a hard hat on the head (`top` is the top of the head) and a stack
 * of blocks beside it, dropped in one at a time.
 */
const drawBuilding = (
  hat: SVGGElement | null,
  blocks: (SVGRectElement | null)[],
  [x, top]: [number, number],
  ground: number,
  blockX: number,
  t: number,
  { hat: hatAmount, blocks: amount }: { hat: number; blocks: number },
) => {
  set(hat, {
    transform: `translate(${x} ${top - (1 - hatAmount) * 14})`,
    opacity: hatAmount,
  });
  const cycle = (t % BUILD_CYCLE) / BUILD_CYCLE;
  blocks.forEach((block, i) => {
    // Block i drops during its third of the cycle and then rests.
    const p = Math.min(1, Math.max(0, cycle * 3.6 - i * 1.1));
    const fall = (1 - p * p) * 22;
    const fade = cycle > 0.92 ? (1 - cycle) / 0.08 : 1;
    set(block ?? null, {
      x: blockX - BLOCK_W / 2 + (i === 1 ? 1.5 : 0),
      y: ground - (i + 1) * BLOCK_H - fall,
      opacity: amount * (p > 0 ? 1 : 0) * fade,
    });
  });
};

const Building: React.FC<{
  hat: React.RefObject<SVGGElement | null>;
  blocks: React.RefObject<(SVGRectElement | null)[]>;
}> = ({ hat, blocks }) => (
  <>
    <g ref={hat} opacity={0}>
      <path d="M -12 2 Q -12 -11 0 -11 Q 12 -11 12 2 Z" fill={HARD_HAT} />
      <rect x={-16} y={0} width={32} height={4} rx={2} fill={HARD_HAT} />
      <rect x={-1.5} y={-11} width={3} height={11} fill="rgba(0,0,0,0.12)" />
    </g>
    {[0, 1, 2].map((i) => (
      <rect
        key={`block${i}`}
        ref={(el) => {
          blocks.current[i] = el;
        }}
        width={BLOCK_W}
        height={BLOCK_H}
        rx={1.5}
        fill={BLOCK}
        stroke="rgba(0,0,0,0.15)"
        strokeWidth={0.8}
        opacity={0}
      />
    ))}
  </>
);

/** Scanning: sheets of paper fly past behind the helper, left to right. */
const PAPER_TRIP = 1.8;
const drawPapers = (
  els: (SVGGElement | null)[],
  cx: number,
  cy: number,
  t: number,
  amount: number,
) => {
  els.forEach((el, i) => {
    const p = (t / PAPER_TRIP + i / els.length) % 1;
    const x = cx - 48 + 96 * p;
    const y = cy - 12 + i * 11 + 4 * Math.sin(p * Math.PI * 2 + i);
    const edge = Math.min(1, Math.sin(p * Math.PI) * 3);
    set(el, {
      transform: `translate(${x} ${y}) rotate(${-15 + 30 * p + i * 8})`,
      opacity: amount * edge,
    });
  });
};

const Papers: React.FC<{ refs: React.RefObject<(SVGGElement | null)[]> }> = ({
  refs,
}) => (
  <>
    {[0, 1, 2].map((i) => (
      <g
        key={`paper${i}`}
        ref={(el) => {
          refs.current[i] = el;
        }}
        opacity={0}
      >
        <rect
          x={-6}
          y={-7.5}
          width={12}
          height={15}
          rx={1}
          fill="#FFFFFF"
          stroke="rgba(0,0,0,0.4)"
          strokeWidth={1}
        />
        <path
          d="M -3.5 -3.5 H 3.5 M -3.5 0 H 3.5 M -3.5 3.5 H 1.5"
          stroke="rgba(0,0,0,0.4)"
          strokeWidth={1}
        />
      </g>
    ))}
  </>
);

/** The refs and eased state behind every helper's extras. */
const useExtras = () => ({
  sparkles: useRef<(SVGPathElement | null)[]>([]),
  hat: useRef<SVGGElement>(null),
  blocks: useRef<(SVGRectElement | null)[]>([]),
  papers: useRef<(SVGGElement | null)[]>([]),
  questions: useRef<(SVGTextElement | null)[]>([]),
  snores: useRef<(SVGTextElement | null)[]>([]),
  glasses: useRef<SVGPathElement>(null),
  costume: useRef<SVGGElement>(null),
  amount: useRef<Extras>({ ...NO_EXTRAS }),
});
type ExtrasRefs = ReturnType<typeof useExtras>;

/** Eases and draws every extra around a helper. */
const drawExtras = (
  extras: ExtrasRefs,
  mood: AiMood,
  frame: { t: number; dt: number },
  place: {
    /** Centre of the face. */
    face: [number, number];
    /** Top of the head; null for a helper without one (no hard hat). */
    top: [number, number] | null;
    ground: number;
    /**
     * Where the theme's hat sits, when not on {@link top}: for helpers
     * without a head for the hard hat.
     */
    costume?: [number, number];
    /** False when the helper builds with its own body instead of blocks. */
    blocks?: boolean;
    /** The eyes, for the reading glasses. Absent where glasses do not fit. */
    eyes?: EyePlace;
  },
) => {
  const amount = extras.amount.current;
  easeToward(amount, extrasOf(mood), frame.dt, 4);
  const [fx, fy] = place.face;
  drawSparkles(extras.sparkles.current, fx, fy, frame.t, amount.sparkle);
  drawQuestions(extras.questions.current, fx, fy, frame.t, amount.wonder);
  drawSnores(extras.snores.current, fx, fy, frame.t, amount.sleep);
  drawBuilding(
    extras.hat.current,
    extras.blocks.current,
    place.top ?? [fx, fy],
    place.ground,
    fx + 31,
    frame.t,
    {
      hat: place.top === null ? 0 : amount.build,
      blocks: place.blocks === false ? 0 : amount.build,
    },
  );
  // The theme's hat comes off while the hard hat is on.
  const costume = place.costume ?? place.top;
  set(extras.costume.current, {
    transform: costume ? `translate(${costume[0]} ${costume[1]})` : '',
    opacity: costume ? (place.top === null ? 1 : 1 - amount.build) : 0,
  });
  drawPapers(extras.papers.current, fx, fy + 4, frame.t, amount.papers);
  drawGlasses(extras.glasses.current, place.eyes, amount.papers);
};

/** Behind the helper: the flying papers. */
const ExtrasBehind: React.FC<{ extras: ExtrasRefs }> = ({ extras }) => (
  <Papers refs={extras.papers} />
);

/**
 * In front of the helper: the theme's hat, glasses, sparkles, question marks,
 * z's, hard hat and blocks.
 */
const ExtrasFront: React.FC<{ extras: ExtrasRefs }> = ({ extras }) => {
  const theme = useAiTheme();
  return (
    <>
      <ThemeHat ref={extras.costume} hat={theme.hat} />
      <path
        ref={extras.glasses}
        fill="rgba(255,255,255,0.18)"
        stroke={INK}
        strokeLinecap="round"
        opacity={0}
      />
      <Sparkles refs={extras.sparkles} />
      {QUESTIONS.map((_, i) => (
        <text
          key={`question${i}`}
          ref={(el) => {
            extras.questions.current[i] = el;
          }}
          textAnchor="middle"
          dominantBaseline="central"
          fontWeight={700}
          fill={INK}
          opacity={0}
        >
          ?
        </text>
      ))}
      {SNORES.map((_, i) => (
        <text
          key={`snore${i}`}
          ref={(el) => {
            extras.snores.current[i] = el;
          }}
          textAnchor="middle"
          dominantBaseline="central"
          fontWeight={700}
          fill={INK}
          opacity={0}
        >
          z
        </text>
      ))}
      <Building hat={extras.hat} blocks={extras.blocks} />
    </>
  );
};

/** Three-dot typing indicator heights. */
const typingDot = (t: number, i: number) =>
  Math.max(0, Math.sin(t * 6 - i * 0.9));

// ---------------------------------------------------------------------------
// Body language shared by Blob, Eyes and Capsule.
// ---------------------------------------------------------------------------

type Body = {
  /** Jumps per second; 0 is no hopping. */
  hop: number;
  hopHeight: number;
  /** Degrees of lean. */
  lean: number;
  /** Stretch: positive is tall and thin. */
  stretch: number;
  /** Sink toward the ground, px. */
  sink: number;
  wobble: number;
  /** Side-to-side sway, px. */
  sway: number;
};

const BODIES: Record<AiMood, Body> = {
  idle: {
    hop: 0,
    hopHeight: 0,
    lean: 0,
    stretch: 0,
    sink: 0,
    wobble: 0.018,
    sway: 0,
  },
  eager: {
    hop: 1.6,
    hopHeight: 6,
    lean: 0,
    stretch: 0.06,
    sink: 0,
    wobble: 0.02,
    sway: 0,
  },
  curious: {
    hop: 0,
    hopHeight: 0,
    lean: 8,
    stretch: 0.04,
    sink: 0,
    wobble: 0.02,
    sway: 0,
  },
  scanning: {
    hop: 0,
    hopHeight: 0,
    lean: 0,
    stretch: 0,
    sink: 1,
    wobble: 0.015,
    sway: 3,
  },
  // Small busy taps, leaning toward the stack.
  building: {
    hop: 1.4,
    hopHeight: 1.5,
    lean: 4,
    stretch: 0,
    sink: 0,
    wobble: 0.015,
    sway: 0,
  },
  thinking: {
    hop: 0,
    hopHeight: 0,
    lean: -7,
    stretch: -0.02,
    sink: 1,
    wobble: 0.012,
    sway: 0,
  },
  excited: {
    hop: 2.2,
    hopHeight: 9,
    lean: 0,
    stretch: 0.05,
    sink: 0,
    wobble: 0.03,
    sway: 0,
  },
  happy: {
    hop: 0.9,
    hopHeight: 4,
    lean: 0,
    stretch: 0.02,
    sink: 0,
    wobble: 0.02,
    sway: 0,
  },
  proud: {
    hop: 0,
    hopHeight: 0,
    lean: 0,
    stretch: 0.08,
    sink: -2,
    wobble: 0.012,
    sway: 0,
  },
  // Deflates onto the ground: wide and low.
  sad: {
    hop: 0,
    hopHeight: 0,
    lean: 0,
    stretch: -0.32,
    sink: 3,
    wobble: 0.006,
    sway: 0,
  },
  // Settles low with its head tipped to one side, barely moving.
  sleeping: {
    hop: 0,
    hopHeight: 0,
    lean: -9,
    stretch: -0.1,
    sink: 2,
    wobble: 0.008,
    sway: 0,
  },
};

/** Height and squash of a hop at phase p: crouch, fly, land, rest. */
const hopCurve = (p: number): [number, number] => {
  if (p < 0.15) return [0, -0.12 * Math.sin((p / 0.15) * Math.PI)];
  if (p < 0.6) {
    const q = (p - 0.15) / 0.45;
    return [Math.sin(q * Math.PI), 0.08 * Math.sin(q * Math.PI)];
  }
  if (p < 0.75) return [0, -0.14 * Math.sin(((p - 0.6) / 0.15) * Math.PI)];
  return [0, 0];
};

// ---------------------------------------------------------------------------
// 1. Blob: soft blob with flat eyes and real body animation.
// ---------------------------------------------------------------------------

const BLOB_POINTS = 12;
const BLOB_PHASES = Array.from({ length: BLOB_POINTS }, (_, i) => i * 2.399);
const BLOB_R = 28;

export const AiBlob: AiCharacterComponent = (props) => {
  const theme = useAiTheme();
  const stage = useRef<HTMLDivElement>(null);
  const extras = useExtras();
  const body = useRef<SVGPathElement>(null);
  const shadow = useRef<SVGEllipseElement>(null);
  const eyes = useRef<(SVGPathElement | null)[]>([]);
  const dots = useRef<(SVGCircleElement | null)[]>([]);
  const state = useRef({
    body: { ...BODIES.idle },
    eye: { ...EYES.idle(0) },
    loader: { ...LOADERS.idle },
    lump: 0,
    shiver: createShiver(),
    hopPhase: 0,
    talk: 0,
    spring: createSpring(),
    wasTalking: false,
    blink: createBlinker(),
  });

  useCharacterLoop(props, stage, ({ t, dt, now, talking, mood, gaze }) => {
    const s = state.current;
    easeToward(s.body, BODIES[mood], dt, 4);
    easeToward(s.eye, gazeEye(EYES[mood](t), gaze), dt, 7);
    easeToward(s.loader, LOADERS[mood], dt, 4);
    s.lump += ((LUMP[mood] ?? 0) - s.lump) * (1 - Math.exp(-dt * 3));
    s.talk += ((talking ? 1 : 0) - s.talk) * 0.3;
    if (talking && !s.wasTalking) s.spring.kick(2.5);
    s.wasTalking = talking;
    s.hopPhase = (s.hopPhase + dt * s.body.hop) % 1;
    const [lift, hopSquash] = s.body.hop > 0.05 ? hopCurve(s.hopPhase) : [0, 0];
    const squash =
      hopSquash +
      s.spring.step(dt) * 0.04 +
      0.05 * s.talk * Math.sin(t * 11) +
      s.body.stretch +
      0.015 * Math.sin(t * 2);
    // Sad, it slumps further than the shared body does: a heap on the floor.
    const sx = (1 - squash * 0.7) * (1 + 0.15 * s.lump);
    const sy = (1 + squash) * (1 - 0.3 * s.lump);
    const y = -lift * s.body.hopHeight + s.body.sink;
    const x = s.body.sway * Math.sin(t * 1.4) + s.shiver(now, s.lump);
    const ground = CY + BLOB_R;
    // Scale from the ground so it squashes onto the floor.
    const points = BLOB_PHASES.map((phase, i): [number, number] => {
      const angle = (i / BLOB_POINTS) * Math.PI * 2;
      // Verbs: a bulge runs around the outline like a loader.
      const r =
        BLOB_R *
        (1 +
          s.body.wobble * Math.sin(t * 2.6 + phase) +
          0.08 *
            s.loader.scan *
            Math.pow(Math.max(0, Math.cos(angle - t * 6)), 3) +
          0.06 * s.loader.think * Math.sin(3 * angle - t * 3) +
          s.lump * lumpiness(angle));
      const py = ground + y - (BLOB_R - Math.sin(angle) * r) * sy;
      // The lower half settles flat onto the floor.
      const settle = Math.sin(angle) > 0 ? s.lump * 0.85 : 0;
      return [
        CX + x + Math.cos(angle) * r * sx,
        py + (ground + y - py) * settle,
      ];
    });
    set(body.current, {
      d: smoothPath(points),
      transform: `rotate(${s.body.lean.toFixed(2)} ${CX} ${ground})`,
    });
    set(shadow.current, {
      rx: 22 * sx * (1 - lift * 0.3),
      opacity: 1 - lift * 0.4,
    });
    const head: [number, number] = [
      CX + x + s.body.lean * 0.5,
      ground + y - BLOB_R * sy * 1.2,
    ];
    drawEyes(eyes.current, s.eye, head, 9, s.blink(now));
    const crown = ground + y - 2 * BLOB_R * sy;
    // Thinking: three dots above the head, like the other helpers.
    dots.current.forEach((dot, i) => {
      set(dot, {
        cx: head[0] - 8 + i * 8,
        cy: crown - 8 - 4 * typingDot(t, i),
        opacity: s.loader.think,
      });
    });
    drawExtras(
      extras,
      mood,
      { t, dt },
      {
        face: head,
        top: [head[0], crown + 3],
        ground,
        eyes: { eye: s.eye, centre: head, spacing: 9, scale: 1 },
      },
    );
  });

  return (
    <Stage ref={stage}>
      <ellipse ref={shadow} cx={CX} cy={CY + 31} rx={22} ry={3} fill={SHADOW} />
      <ExtrasBehind extras={extras} />
      <path ref={body} fill={theme.body} />
      <EyePair refs={eyes} />
      {[0, 1, 2].map((index) => (
        <circle
          key={`d${index}`}
          ref={(el) => {
            dots.current[index] = el;
          }}
          r={2.4}
          fill={INK}
          opacity={0}
        />
      ))}
      <ExtrasFront extras={extras} />
    </Stage>
  );
};

// ---------------------------------------------------------------------------
// 2. Morph: the body morphs between strict shapes, flat eyes, no mouth.
// ---------------------------------------------------------------------------

const MORPH_SAMPLES = 40;
/** How far each shape sits below the centre, px, so the puddle lies on the ground. */
const MORPH_SINK: Partial<Record<AiMood, number>> = { sad: 17 };
const MORPH_RADIUS = 29;

const polygonRadius = (theta: number, sides: number, rotation: number) => {
  const sector = (2 * Math.PI) / sides;
  const local =
    ((((theta - rotation) % sector) + sector) % sector) - sector / 2;
  return Math.cos(Math.PI / sides) / Math.cos(local);
};

const superellipse = (theta: number, n: number) =>
  Math.pow(
    Math.pow(Math.abs(Math.cos(theta)), n) +
      Math.pow(Math.abs(Math.sin(theta)), n),
    -1 / n,
  ) * 0.92;

/** Radius factor of each mood's shape at an angle (y points down). */
const MORPH_SHAPES: Record<AiMood, (theta: number, t: number) => number> = {
  idle: () => 1,
  eager: (theta, t) => 1 + 0.1 * Math.cos(6 * theta + t * 3),
  curious: (theta) => superellipse(theta, 4),
  // Scanning spins like a loader; the eyes stay upright.
  scanning: (theta, t) => superellipse(theta - t * 1.8, 6),
  // A brick.
  building: (theta) => superellipse(theta, 5),
  // Excited's waves, as tall, but only over the crown and travelling: the
  // head mulls it over.
  thinking: (theta, t) =>
    1 +
    0.2 *
      Math.cos(5 * theta + t * 3.2) *
      Math.min(1, Math.max(0, -Math.sin(theta) * 1.6)),
  excited: (theta, t) =>
    1 + 0.15 * Math.cos(5 * theta) * (1 + 0.35 * Math.sin(t * 7)),
  happy: (theta) => 1 + 0.05 * Math.sin(theta),
  proud: (theta) => 0.85 * polygonRadius(theta, 4, 0) + 0.15,
  // A lump of dough: wide, uneven, flat where it rests on the ground.
  sad: (theta) => {
    const puddle =
      1 /
      Math.sqrt(
        Math.pow(Math.cos(theta) / 1.4, 2) +
          Math.pow(Math.sin(theta) / 0.45, 2),
      );
    const r = puddle * (1 + lumpiness(theta));
    const down = Math.sin(theta);
    return down > 0.05 ? Math.min(r, 0.42 / down) : r;
  },
  // A soft, slightly flattened circle that breathes slowly.
  sleeping: (theta, t) =>
    (1 + 0.035 * Math.sin(t * 1.4)) /
    Math.sqrt(
      Math.pow(Math.cos(theta) / 1.08, 2) + Math.pow(Math.sin(theta) / 0.9, 2),
    ),
};

export const AiMorph: AiCharacterComponent = (props) => {
  const theme = useAiTheme();
  const stage = useRef<HTMLDivElement>(null);
  const extras = useExtras();
  const body = useRef<SVGPathElement>(null);
  const shadow = useRef<SVGEllipseElement>(null);
  const eyes = useRef<(SVGPathElement | null)[]>([]);
  const state = useRef({
    radii: Array.from({ length: MORPH_SAMPLES }, () => 1),
    eye: { ...EYES.idle(0) },
    talk: 0,
    sink: 0,
    lump: 0,
    shiver: createShiver(),
    blink: createBlinker(),
  });

  useCharacterLoop(props, stage, ({ t, dt, now, talking, mood, gaze }) => {
    const s = state.current;
    const shape = MORPH_SHAPES[mood];
    s.sink += ((MORPH_SINK[mood] ?? 0) - s.sink) * (1 - Math.exp(-dt * 4));
    s.lump += ((LUMP[mood] ?? 0) - s.lump) * (1 - Math.exp(-dt * 3));
    const shake = s.shiver(now, s.lump);
    const k = 1 - Math.exp(-dt * (mood === 'thinking' ? 12 : 5));
    s.radii.forEach((r, i) => {
      const theta = (i / MORPH_SAMPLES) * Math.PI * 2;
      s.radii[i] = r + (shape(theta, t) - r) * k;
    });
    easeToward(s.eye, gazeEye(EYES[mood](t), gaze), dt, 7);
    s.talk += ((talking ? 1 : 0) - s.talk) * 0.3;
    const breathe = 1 + 0.02 * Math.sin(t * 2);
    const pulse = 1 + 0.07 * s.talk * syllable(t);
    const bob = 1.2 * Math.sin(t * 2.2) - 2 * s.talk * syllable(t);
    const path = s.radii.map((r, i): [number, number] => {
      const theta = (i / MORPH_SAMPLES) * Math.PI * 2;
      return [
        CX + shake + Math.cos(theta) * MORPH_RADIUS * r * breathe * pulse,
        CY +
          bob +
          s.sink +
          (Math.sin(theta) * MORPH_RADIUS * r * breathe) / pulse,
      ];
    });
    set(body.current, { d: smoothPath(path) });
    set(shadow.current, { rx: 22 * pulse - bob * 0.5 });
    // The lump is heavier on one side; the eyes sit a little off centre,
    // on top of the heap rather than down on the floor.
    const morphEyes: EyePlace = {
      eye: s.eye,
      centre: [CX + shake - 2 * s.lump, CY - 3 + bob + s.sink * 0.75],
      spacing: 10,
      scale: 1.15,
    };
    drawEyes(
      eyes.current,
      morphEyes.eye,
      morphEyes.centre,
      morphEyes.spacing,
      s.blink(now),
      morphEyes.scale,
    );
    const crown = path[Math.round(MORPH_SAMPLES * 0.75)] ?? [
      CX,
      CY - MORPH_RADIUS,
    ];
    drawExtras(
      extras,
      mood,
      { t, dt },
      {
        face: [CX, CY - 3 + bob],
        top: [CX + shake, crown[1] + 3],
        ground: CY + 33,
        eyes: morphEyes,
      },
    );
  });

  return (
    <Stage ref={stage}>
      <ellipse ref={shadow} cx={CX} cy={CY + 36} rx={22} ry={3} fill={SHADOW} />
      <ExtrasBehind extras={extras} />
      <path ref={body} fill={theme.body} />
      <EyePair refs={eyes} />
      <ExtrasFront extras={extras} />
    </Stage>
  );
};

// ---------------------------------------------------------------------------
// 3. Eyes: no body at all, two floating eyes with thin brows.
// ---------------------------------------------------------------------------

export const AiEyes: AiCharacterComponent = (props) => {
  const stage = useRef<HTMLDivElement>(null);
  const extras = useExtras();
  const eyes = useRef<(SVGPathElement | null)[]>([]);
  const brows = useRef<(SVGPathElement | null)[]>([]);
  const dots = useRef<(SVGCircleElement | null)[]>([]);
  const sweep = useRef<SVGCircleElement>(null);
  const shadow = useRef<SVGEllipseElement>(null);
  const state = useRef({
    eye: { ...EYES.idle(0) },
    body: { ...BODIES.idle },
    loader: { ...LOADERS.idle },
    lump: 0,
    shiver: createShiver(),
    hopPhase: 0,
    talk: 0,
    blink: createBlinker(),
  });

  useCharacterLoop(props, stage, ({ t, dt, now, talking, mood, gaze }) => {
    const s = state.current;
    easeToward(s.eye, gazeEye(EYES[mood](t), gaze), dt, 7);
    easeToward(s.body, BODIES[mood], dt, 4);
    easeToward(s.loader, LOADERS[mood], dt, 5);
    s.lump += ((LUMP[mood] ?? 0) - s.lump) * (1 - Math.exp(-dt * 3));
    s.talk += ((talking ? 1 : 0) - s.talk) * 0.3;
    s.hopPhase = (s.hopPhase + dt * s.body.hop) % 1;
    const [lift] = s.body.hop > 0.05 ? hopCurve(s.hopPhase) : [0, 0];
    const float = 2 * Math.sin(t * 1.8) * (1 - 0.7 * s.lump);
    const y =
      CY -
      2 +
      float -
      lift * s.body.hopHeight +
      s.body.sink -
      // A squashed body (sad) sinks the floating eyes toward the ground.
      Math.min(0, s.body.stretch) * 40 -
      2.5 * s.talk * syllable(t);
    const x = CX + s.body.sway * Math.sin(t * 1.4) + s.shiver(now, s.lump);
    // Talking squashes the eyes in rhythm, like a mouth would move.
    const eye = { ...s.eye, h: s.eye.h * (1 - 0.18 * s.talk * syllable(t)) };
    drawEyes(eyes.current, eye, [x, y], 11, s.blink(now), 1.6);
    drawExtras(
      extras,
      mood,
      { t, dt },
      {
        face: [x, y],
        top: null,
        costume: [x, y - 21],
        ground: CY + 30,
        eyes: { eye, centre: [x, y], spacing: 11, scale: 1.6 },
      },
    );
    [-1, 1].forEach((side, index) => {
      const bx = x + side * 11 + s.eye.lookX * 0.6;
      const by = y - 13 - s.eye.brow + s.eye.lookY * 0.4;
      set(brows.current[index] ?? null, {
        d: `M ${(bx - 6).toFixed(2)} ${by.toFixed(2)} Q ${bx.toFixed(2)} ${(by - 2).toFixed(2)} ${(bx + 6).toFixed(2)} ${by.toFixed(2)}`,
        transform: `rotate(${(side * s.eye.tilt * 0.9).toFixed(2)} ${bx.toFixed(2)} ${by.toFixed(2)})`,
      });
    });
    // Scanning: a dot sweeps under the eyes like a progress bar.
    set(sweep.current, {
      cx: x + 15 * Math.sin(t * 3.2),
      cy: y + 17,
      opacity: s.loader.scan,
    });
    // Thinking: the three-dot typing indicator.
    dots.current.forEach((dot, i) => {
      set(dot, {
        cx: x - 8 + i * 8,
        cy: y + 18 - 4 * typingDot(t, i),
        opacity: s.loader.think,
      });
    });
    set(shadow.current, { rx: 16 - float * 0.8 - lift * 4 });
  });

  return (
    <Stage ref={stage}>
      <ellipse
        ref={shadow}
        cx={CX}
        cy={CY + 30}
        rx={16}
        ry={2.5}
        fill={SHADOW}
      />
      <ExtrasBehind extras={extras} />
      <EyePair refs={eyes} />
      {[0, 1].map((index) => (
        <path
          key={index}
          ref={(el) => {
            brows.current[index] = el;
          }}
          fill="none"
          stroke={INK}
          strokeWidth={2.2}
          strokeLinecap="round"
        />
      ))}
      <circle ref={sweep} r={2.6} fill={INK} opacity={0} />
      <ExtrasFront extras={extras} />
      {[0, 1, 2].map((index) => (
        <circle
          key={`d${index}`}
          ref={(el) => {
            dots.current[index] = el;
          }}
          r={2.4}
          fill={INK}
          opacity={0}
        />
      ))}
    </Stage>
  );
};

// ---------------------------------------------------------------------------
// 4. Capsule: a flat capsule that speaks with body language.
// ---------------------------------------------------------------------------

const CAPSULE_W = 34;
const CAPSULE_H = 50;
const CAPSULE_POINTS = 32;

/** Sad: half the base and the height of the sand heap it slumps into. */
const HEAP_LEFT = 31;
const HEAP_RIGHT = 26;
const HEAP_H = 21;

/**
 * Distance along a ray from the centre of a rounded rectangle (half sizes
 * a and b, corner radius the smaller of them) to its edge.
 */
const roundedRectRay = (c: number, s: number, a: number, b: number) => {
  const r = Math.min(a, b);
  if (Math.abs(c) > 1e-6) {
    const t = a / Math.abs(c);
    if (Math.abs(t * s) <= b - r) return t;
  }
  if (Math.abs(s) > 1e-6) {
    const t = b / Math.abs(s);
    if (Math.abs(t * c) <= a - r) return t;
  }
  const kx = Math.sign(c) * (a - r);
  const ky = Math.sign(s) * (b - r);
  const along = c * kx + s * ky;
  return (
    along + Math.sqrt(Math.max(0, along * along - kx * kx - ky * ky + r * r))
  );
};

/** Height of the heap at dx from its peak: wide feet, sloping sides, a soft top. */
const heapProfile = (dx: number) => {
  const half = dx < 0 ? HEAP_LEFT : HEAP_RIGHT;
  const u = Math.abs(dx) / half;
  if (u >= 1) return 0;
  // A few fixed ripples, like settled sand; the same every frame.
  const grain =
    1 + 0.04 * Math.sin(dx * 0.45 + 0.8) + 0.025 * Math.sin(dx * 1.1);
  return HEAP_H * Math.pow(1 - u * u, 1.7) * grain;
};

/** Distance along a ray from (px, py) to the edge of the heap on `ground`. */
const heapRay = (
  px: number,
  py: number,
  c: number,
  s: number,
  ground: number,
) => {
  let inside = 0;
  let outside = 60;
  for (let i = 0; i < 18; i++) {
    const t = (inside + outside) / 2;
    const x = c * t;
    const y = py + s * t;
    if (y <= ground && y >= ground - heapProfile(x)) inside = t;
    else outside = t;
  }
  return inside;
};

/**
 * The capsule outline, standing on `ground`. Sad (`lump` 1) it slumps into a
 * heap of sand. Both shapes are sampled at the same angles from their centres
 * and blended point by point, so nothing slides along the edge.
 */
const capsuleOutline = (
  cx: number,
  ground: number,
  w: number,
  h: number,
  lump: number,
): [number, number][] => {
  const middle = ground - h / 2;
  const heapY = ground - HEAP_H * 0.4;
  return Array.from({ length: CAPSULE_POINTS }, (_, i): [number, number] => {
    const angle = (i / CAPSULE_POINTS) * Math.PI * 2;
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const tc = roundedRectRay(c, s, w / 2, h / 2);
    const capsule: [number, number] = [cx + c * tc, middle + s * tc];
    if (lump < 0.01) return capsule;
    const th = heapRay(0, heapY, c, s, ground);
    const heap: [number, number] = [cx + c * th, heapY + s * th];
    return [
      capsule[0] + (heap[0] - capsule[0]) * lump,
      capsule[1] + (heap[1] - capsule[1]) * lump,
    ];
  });
};

/** Scanning, it spins like a barrel: the eyes slide round and come back. */
export const AiCapsule: AiCharacterComponent = (props) => {
  const theme = useAiTheme();
  const stage = useRef<HTMLDivElement>(null);
  const extras = useExtras();
  const body = useRef<SVGPathElement>(null);
  const group = useRef<SVGGElement>(null);
  const shadow = useRef<SVGEllipseElement>(null);
  const eyes = useRef<(SVGPathElement | null)[]>([]);
  const dots = useRef<(SVGCircleElement | null)[]>([]);
  const state = useRef({
    body: { ...BODIES.idle },
    eye: { ...EYES.idle(0) },
    loader: { ...LOADERS.idle },
    lump: 0,
    shiver: createShiver(),
    hopPhase: 0,
    talk: 0,
    spring: createSpring(160, 10),
    wasTalking: false,
    blink: createBlinker(),
  });

  useCharacterLoop(props, stage, ({ t, dt, now, talking, mood, gaze }) => {
    const s = state.current;
    easeToward(s.body, BODIES[mood], dt, 4);
    easeToward(s.eye, gazeEye(EYES[mood](t), gaze), dt, 7);
    easeToward(s.loader, LOADERS[mood], dt, 5);
    s.lump += ((LUMP[mood] ?? 0) - s.lump) * (1 - Math.exp(-dt * 3));
    s.talk += ((talking ? 1 : 0) - s.talk) * 0.3;
    if (talking && !s.wasTalking) s.spring.kick(3);
    s.wasTalking = talking;
    s.hopPhase = (s.hopPhase + dt * s.body.hop) % 1;
    const [lift, hopSquash] = s.body.hop > 0.05 ? hopCurve(s.hopPhase) : [0, 0];
    const squash =
      hopSquash * 1.5 +
      s.spring.step(dt) * 0.05 +
      0.06 * s.talk * Math.sin(t * 11) +
      s.body.stretch * 1.4 +
      0.012 * Math.sin(t * 2);
    const scan = s.loader.scan;
    const w = CAPSULE_W * (1 - squash * 0.7);
    const h = CAPSULE_H * (1 + squash) - s.body.sink * 1.5;
    const ground = CY + CAPSULE_H / 2 + 2;
    // A rolling barrel stays upright; other moods may sway.
    const sway = s.body.sway * (1 - scan);
    // The heap itself lies still; only the eyes sob.
    const sob = s.shiver(now, s.lump);
    const x = CX + sway * Math.sin(t * 1.4);
    const y = -lift * s.body.hopHeight;
    const lean = s.body.lean + sway * 0.8 * Math.sin(t * 1.4);
    set(group.current, {
      transform: `translate(0 ${y.toFixed(2)}) rotate(${lean.toFixed(2)} ${x.toFixed(2)} ${ground.toFixed(2)})`,
    });
    set(body.current, {
      d: smoothPath(capsuleOutline(x, ground, w, h, s.lump)),
    });
    // The eyes stay up on the heap, a little toward its peak.
    const top = ground - h + (ground - HEAP_H - (ground - h)) * s.lump;
    const eyeY =
      ground - h + 17 + (ground - HEAP_H * 0.62 - (ground - h + 17)) * s.lump;
    const blink = s.blink(now);
    const eye = s.eye;
    if (scan > 0.01) {
      // Spinning barrel: each eye travels round the body, narrows toward
      // the edge and hides behind it, then comes back from the other side.
      const spin = t * 2.4;
      [-1, 1].forEach((side, index) => {
        const angle = spin + side * 0.5;
        const front = Math.cos(angle);
        const rolledX = x + Math.sin(angle) * (w / 2 - 3);
        const restX = x + side * 7 + eye.lookX * 0.3;
        const ex = restX * (1 - scan) + rolledX * scan;
        const widthScale = 1 - scan + scan * Math.max(0, front);
        const size = 0.95;
        const hh = Math.max(0.5, eye.h * size * (1 - 0.88 * blink));
        set(eyes.current[index] ?? null, {
          d: eyePath(
            ex,
            eyeY + eye.lookY,
            Math.max(0.01, eye.w * size * widthScale),
            hh,
            eye.top,
            eye.bottom,
          ),
          transform: '',
          opacity: front > -0.05 || scan < 0.5 ? 1 : 0,
        });
      });
    } else {
      eyes.current.forEach((el) => set(el, { opacity: 1 }));
      drawEyes(
        eyes.current,
        eye,
        [x + eye.lookX * 0.3 + sob - 1.5 * s.lump, eyeY],
        7,
        blink,
        0.95,
      );
    }
    // Thinking: three dots above the head.
    dots.current.forEach((dot, i) => {
      set(dot, {
        cx: x - 7 + i * 7,
        cy: top - 8 - 4 * typingDot(t, i),
        opacity: s.loader.think,
      });
    });
    set(shadow.current, {
      rx: 18 * (1 - lift * 0.3) * (w / CAPSULE_W) * (1 - s.lump) + 30 * s.lump,
    });
    drawExtras(
      extras,
      mood,
      { t, dt },
      {
        face: [x, eyeY - 3],
        top: [x, top + 3],
        ground,
      },
    );
  });

  return (
    <Stage ref={stage}>
      <ellipse
        ref={shadow}
        cx={CX}
        cy={CY + CAPSULE_H / 2 + 3}
        rx={18}
        ry={3}
        fill={SHADOW}
      />
      <g ref={group}>
        <ExtrasBehind extras={extras} />
        <path ref={body} fill={theme.body} />
        <EyePair refs={eyes} />
        {[0, 1, 2].map((index) => (
          <circle
            key={`d${index}`}
            ref={(el) => {
              dots.current[index] = el;
            }}
            r={2.2}
            fill={INK}
            opacity={0}
          />
        ))}
        <ExtrasFront extras={extras} />
      </g>
    </Stage>
  );
};

// ---------------------------------------------------------------------------
// 5. Logo: the Nodon N come alive. Seven nodes and four strokes, no face:
// every mood is where the nodes go and how they move.
// ---------------------------------------------------------------------------

/** The logo drawn at this many px per logo unit, keeping its proportions. */
const LOGO_SCALE = 3.2;
const LOGO_NODES: [number, number][] = [
  [2.1028, 2.1028],
  [2.1028, 7.5],
  [2.1028, 12.8972],
  [7.07944, 7.5],
  [12.0561, 2.1028],
  [12.0561, 7.5],
  [12.0561, 12.8972],
].map(([x, y]) => [(x! - 7.07944) * LOGO_SCALE, (y! - 7.5) * LOGO_SCALE]);
const LOGO_STROKES: [number, number][] = [
  [0, 2],
  [0, 3],
  [3, 6],
  [4, 6],
];
/** The logo's dot radius (2.1) and stroke (0.63) at the same scale. */
const NODE_R = 2.1028 * LOGO_SCALE;
const STROKE = 0.63 * LOGO_SCALE;
/** Where the logo stands: the bottom nodes rest on this line. */
const LOGO_FLOOR = LOGO_NODES[2]![1];

type Layout = { nodes: [number, number][]; strokes: number; rotation: number };

/** The N, scaled about its floor and lifted by `lift` px. */
const logoN = (sx: number, sy: number, lift = 0): [number, number][] =>
  LOGO_NODES.map(([x, y]) => [
    x * sx,
    LOGO_FLOOR + (y - LOGO_FLOOR) * sy - lift,
  ]);

/** A jump of `height` px, `rate` per second, landing hard and leaving soft. */
const jump = (t: number, rate: number, height: number) =>
  height * Math.abs(Math.sin(t * rate * Math.PI));

const LOGO_LAYOUTS: Record<AiMood, (t: number) => Layout> = {
  // Breathes.
  idle: (t) => ({
    nodes: logoN(1, 1 + 0.03 * Math.sin(t * 2.2)),
    strokes: 1,
    rotation: 0,
  }),
  // Stands tall, chest out, and stays still.
  proud: () => ({ nodes: logoN(1.12, 1.18, 2), strokes: 1, rotation: 0 }),
  // Stretched up on its toes; the nodes hop one after another.
  eager: (t) => ({
    nodes: logoN(0.95, 1.2).map(([x, y], i) => [
      x,
      y - 3 * Math.abs(Math.sin(t * 6 + i * 0.8)),
    ]),
    strokes: 0.7,
    rotation: 0,
  }),
  // Tilts its head and leans in, slowly turning it over.
  curious: (t) => ({
    nodes: logoN(1, 1),
    strokes: 1,
    rotation: 11 + 4 * Math.sin(t * 1.3),
  }),
  // Sways side to side with a little bounce on every beat.
  happy: (t) => ({
    nodes: logoN(1.05, 1.05, jump(t, 1.1, 3)),
    strokes: 1,
    rotation: 6 * Math.sin(t * 1.1 * Math.PI),
  }),
  // Jumps high, squashing on landing; the nodes burst out in the air.
  excited: (t) => {
    const air = Math.abs(Math.sin(t * 1.6 * Math.PI));
    const squash = Math.pow(1 - air, 6);
    const burst = 1 + 0.25 * air;
    return {
      nodes: logoN(
        burst * (1 + 0.2 * squash),
        burst * (1 - 0.3 * squash),
        14 * air,
      ),
      strokes: 1 - air * 0.6,
      rotation: 0,
    };
  },
  // Verb: the nodes in a row with a pulse running along them.
  scanning: (t) => ({
    nodes: Array.from({ length: 7 }, (_, i): [number, number] => [
      -40 + i * 13.3,
      8 + 2 * Math.sin(t * 5 - i * 0.9),
    ]),
    strokes: 0,
    rotation: 0,
  }),
  // Verb: the nodes circle like a loader.
  thinking: (t) => ({
    nodes: Array.from({ length: 7 }, (_, i): [number, number] => {
      const angle = t * 1.6 + (i / 7) * Math.PI * 2;
      return [Math.cos(angle) * 24, -2 + Math.sin(angle) * 24];
    }),
    strokes: 0,
    rotation: 0,
  }),
  // It builds a little wall out of its own nodes, one dropped at a time.
  building: (t) => {
    const cycle = (t % BUILD_CYCLE) / BUILD_CYCLE;
    const slots: [number, number][] = [
      [-21, 21],
      [-7, 21],
      [7, 21],
      [21, 21],
      [-14, 7.5],
      [0, 7.5],
      [14, 7.5],
    ];
    return {
      nodes: slots.map(([x, y], i): [number, number] => {
        const p = Math.min(1, Math.max(0, cycle * 8.5 - i));
        // Waiting nodes lie in a heap at the side; each is lifted in an arc
        // into its slot.
        const [hx, hy] = [38, 21];
        return [
          hx + (x - hx) * p,
          hy + (y - hy) * p - 16 * Math.sin(p * Math.PI),
        ];
      }),
      strokes: 0,
      rotation: 0,
    };
  },
  // Collapsed into an untidy pile on the floor.
  sad: () => ({
    nodes: [
      [-18, 23],
      [-4, 24],
      [10, 23],
      [22, 24],
      [-11, 12.5],
      [3, 13.5],
      [-4, 2.5],
    ],
    strokes: 0,
    rotation: 3,
  }),
  // Leans back, a little lower, and breathes slowly.
  sleeping: (t) => ({
    nodes: logoN(1.04, 0.86 + 0.03 * Math.sin(t * 1.4)),
    strokes: 1,
    rotation: -10,
  }),
};

export const AiLogo: AiCharacterComponent = (props) => {
  const theme = useAiTheme();
  const stage = useRef<HTMLDivElement>(null);
  const extras = useExtras();
  const group = useRef<SVGGElement>(null);
  const nodes = useRef<(SVGCircleElement | null)[]>([]);
  const strokes = useRef<(SVGLineElement | null)[]>([]);
  const shadow = useRef<SVGEllipseElement>(null);
  const state = useRef({
    nodes: LOGO_NODES.map(([x, y]) => [x, y] as [number, number]),
    strokes: 1,
    rotation: 0,
    talk: 0,
    lean: 0,
    mood: 'idle' as AiMood,
    settled: 1,
    loader: { ...LOADERS.idle },
    lump: 0,
    shiver: createShiver(),
  });

  useCharacterLoop(props, stage, ({ t, dt, now, talking, mood, gaze }) => {
    const s = state.current;
    const layout = LOGO_LAYOUTS[mood](t);
    s.lump += ((LUMP[mood] ?? 0) - s.lump) * (1 - Math.exp(-dt * 3));
    // Once the nodes have arrived, a moving shape (a loader, a jump) is
    // followed closely so it keeps its form.
    s.settled +=
      ((s.mood === mood ? 1 : 0) - s.settled) * (1 - Math.exp(-dt * 1.5));
    if (s.mood !== mood) s.settled = 0;
    s.mood = mood;
    s.nodes.forEach((node, i) => {
      // Staggered rates make the nodes travel one after another.
      const k = 1 - Math.exp(-dt * (6 + i * 0.7 + 24 * s.settled));
      const [tx, ty] = layout.nodes[i] ?? [0, 0];
      node[0] += (tx - node[0]) * k;
      node[1] += (ty - node[1]) * k;
    });
    const k = 1 - Math.exp(-dt * 5);
    s.strokes += (layout.strokes - s.strokes) * k;
    s.rotation += (layout.rotation - s.rotation) * k;
    // No eyes to look with: it leans toward the pointer instead.
    s.lean += ((gaze === null ? 0 : gaze[0] * 6) - s.lean) * k;
    s.talk += ((talking ? 1 : 0) - s.talk) * 0.3;
    easeToward(s.loader, LOADERS[mood], dt, 5);
    const shake = s.shiver(now, s.lump);
    set(group.current, {
      transform: `translate(${(CX + shake).toFixed(2)} ${CY.toFixed(2)}) rotate(${(s.rotation + s.lean).toFixed(2)} 0 ${LOGO_FLOOR.toFixed(2)})`,
    });
    s.nodes.forEach(([x, y], i) => {
      // Talking: the nodes bounce like a waveform.
      const speak = s.talk * 3 * Math.sin(t * 14 + i * 1.3);
      // Scanning: a highlight runs along the row of nodes.
      const pulse = s.loader.scan * 0.7 * travelling(t, i, 7, 0.9);
      set(nodes.current[i] ?? null, {
        cx: x,
        cy: y + speak,
        r: NODE_R * (1 + pulse),
      });
    });
    LOGO_STROKES.forEach(([a, b], i) => {
      const [x1, y1] = s.nodes[a] ?? [0, 0];
      const [x2, y2] = s.nodes[b] ?? [0, 0];
      set(strokes.current[i] ?? null, {
        x1,
        y1,
        x2,
        y2,
        opacity: Math.max(0, s.strokes),
      });
    });
    const lowest = Math.max(...s.nodes.map(([, y]) => y));
    set(shadow.current, { rx: 22 - Math.max(0, LOGO_FLOOR - lowest) * 0.6 });
    drawExtras(
      extras,
      mood,
      { t, dt },
      {
        face: [0, -6],
        top: null,
        costume: [0, Math.min(...s.nodes.map(([, y]) => y)) - NODE_R + 1],
        ground: 34,
        blocks: false,
      },
    );
  });

  return (
    <Stage ref={stage}>
      <ellipse ref={shadow} cx={CX} cy={CY + 34} rx={22} ry={3} fill={SHADOW} />
      <g ref={group}>
        <ExtrasBehind extras={extras} />
        {LOGO_STROKES.map((_, i) => (
          <line
            key={`s${i}`}
            ref={(el) => {
              strokes.current[i] = el;
            }}
            stroke={theme.body}
            strokeWidth={STROKE}
            strokeLinecap="round"
          />
        ))}
        {LOGO_NODES.map((_, i) => (
          <circle
            key={`n${i}`}
            ref={(el) => {
              nodes.current[i] = el;
            }}
            fill={theme.body}
          />
        ))}
        <ExtrasFront extras={extras} />
      </g>
    </Stage>
  );
};

// ---------------------------------------------------------------------------
// 6. Swirl: every AI company's logo at once. A rosette of curled petals
// around a puckered middle, no face: the middle clenches and opens with the
// mood and opens and closes as it talks.
// ---------------------------------------------------------------------------

const SWIRL_PETALS = 7;

type SwirlPose = {
  /** Degrees per second the rosette turns. */
  spin: number;
  /** How open the middle is: 0 is clenched, 1 is wide open. */
  pucker: number;
  /** Petal length, px. */
  length: number;
  /** How far each petal curls round, radians. */
  twist: number;
  /** Degrees of tilt. */
  tilt: number;
  /** Height above its resting place, px; negative sinks. */
  lift: number;
  /** 0…1: the petals hang down. */
  droop: number;
  /** 0…1: a pulse runs round the petals, like a loader. */
  ripple: number;
};

const SWIRL_POSES: Record<AiMood, (t: number) => SwirlPose> = {
  // Turns slowly and breathes through the middle.
  idle: (t) => ({
    spin: 6,
    pucker: 0.45 + 0.08 * Math.sin(t * 2.2),
    length: 22,
    twist: 0.5,
    tilt: 0,
    lift: 0,
    droop: 0,
    ripple: 0,
  }),
  eager: (t) => ({
    spin: 40,
    pucker: 0.6,
    length: 23,
    twist: 0.6,
    tilt: 0,
    lift: jump(t, 1.6, 6),
    droop: 0,
    ripple: 0,
  }),
  // Tilts, uncurls and opens wide: what is that?
  curious: (t) => ({
    spin: 3,
    pucker: 0.85,
    length: 21,
    twist: 0.3,
    tilt: 12 + 4 * Math.sin(t * 1.3),
    lift: 0,
    droop: 0,
    ripple: 0,
  }),
  // Verb: spins like a loader with a pulse running round.
  scanning: () => ({
    spin: 200,
    pucker: 0.3,
    length: 21,
    twist: 0.9,
    tilt: 0,
    lift: 0,
    droop: 0,
    ripple: 1,
  }),
  // Verb: clenched in concentration, the pulse going round.
  thinking: () => ({
    spin: 30,
    pucker: 0.15,
    length: 20,
    twist: 0.7,
    tilt: -7,
    lift: 0,
    droop: 0,
    ripple: 1,
  }),
  building: (t) => ({
    spin: 15,
    pucker: 0.4,
    length: 21,
    twist: 0.5,
    tilt: 4,
    lift: jump(t, 1.4, 1.5),
    droop: 0,
    ripple: 0,
  }),
  // Jumps and flares, spinning fast, wide open.
  excited: (t) => ({
    spin: 120,
    pucker: 0.9,
    length: 26 + 2 * Math.sin(t * 9),
    twist: 0.8,
    tilt: 0,
    lift: jump(t, 2.2, 9),
    droop: 0,
    ripple: 0,
  }),
  happy: (t) => ({
    spin: 20,
    pucker: 0.65,
    length: 23,
    twist: 0.55,
    tilt: 6 * Math.sin(t * 1.1 * Math.PI),
    lift: jump(t, 0.9, 4),
    droop: 0,
    ripple: 0,
  }),
  // Petals out, still, and smugly clenched.
  proud: () => ({
    spin: 0,
    pucker: 0.05,
    length: 26,
    twist: 0.4,
    tilt: 0,
    lift: 2,
    droop: 0,
    ripple: 0,
  }),
  // Stops turning and wilts onto the floor.
  sad: () => ({
    spin: 0,
    pucker: 0.55,
    length: 19,
    twist: 0.15,
    tilt: 0,
    lift: -6,
    droop: 1,
    ripple: 0,
  }),
  // Barely turns, tipped over, the middle slowly opening and closing.
  sleeping: (t) => ({
    spin: 2,
    pucker: 0.3 + 0.25 * (0.5 + 0.5 * Math.sin(t * 1.4)),
    length: 21,
    twist: 0.45,
    tilt: -10,
    lift: -2,
    droop: 0,
    ripple: 0,
  }),
};

/** One petal: from the rim of the middle out to a tip curled `twist` round. */
const swirlPetal = (
  [cx, cy]: [number, number],
  angle: number,
  inner: number,
  length: number,
  twist: number,
  droop: number,
) => {
  // Drooping pulls every petal toward straight down.
  const towardDown = Math.atan2(
    Math.sin(Math.PI / 2 - angle),
    Math.cos(Math.PI / 2 - angle),
  );
  const a = angle + towardDown * droop * 0.6;
  const at = (r: number, theta: number): [number, number] => [
    cx + r * Math.cos(theta),
    cy + r * Math.sin(theta),
  ];
  const [x0, y0] = at(inner, a);
  const tip = at(inner + length, a + twist);
  tip[1] += droop * length * 0.4;
  // Fat, rounded petals that overlap their neighbours into one soft mark.
  const spread = 11 / (inner + length * 0.5);
  const [lx, ly] = at(inner + length * 0.95, a + twist * 0.55 - spread);
  const [rx, ry] = at(inner + length * 0.95, a + twist * 0.55 + spread);
  const [l0x, l0y] = at(inner + length * 0.2, a - spread * 0.9);
  const [r0x, r0y] = at(inner + length * 0.2, a + spread * 0.9);
  const f = (n: number) => n.toFixed(2);
  return (
    `M ${f(x0)} ${f(y0)}` +
    ` C ${f(l0x)} ${f(l0y)} ${f(lx)} ${f(ly)} ${f(tip[0])} ${f(tip[1])}` +
    ` C ${f(rx)} ${f(ry)} ${f(r0x)} ${f(r0y)} ${f(x0)} ${f(y0)} Z`
  );
};

export const AiSwirl: AiCharacterComponent = (props) => {
  const theme = useAiTheme();
  const stage = useRef<HTMLDivElement>(null);
  const extras = useExtras();
  const group = useRef<SVGGElement>(null);
  const petals = useRef<(SVGPathElement | null)[]>([]);
  const hole = useRef<SVGEllipseElement>(null);
  const shadow = useRef<SVGEllipseElement>(null);
  const state = useRef({
    pose: { ...SWIRL_POSES.idle(0) },
    angle: 0,
    lean: 0,
    talk: 0,
    lump: 0,
    shiver: createShiver(),
  });

  useCharacterLoop(props, stage, ({ t, dt, now, talking, mood, gaze }) => {
    const s = state.current;
    easeToward(s.pose, SWIRL_POSES[mood](t), dt, 8);
    s.angle += ((s.pose.spin * Math.PI) / 180) * dt;
    s.lump += ((LUMP[mood] ?? 0) - s.lump) * (1 - Math.exp(-dt * 3));
    // No eyes to look with: it leans toward the pointer instead.
    s.lean +=
      ((gaze === null ? 0 : gaze[0] * 6) - s.lean) * (1 - Math.exp(-dt * 5));
    s.talk += ((talking ? 1 : 0) - s.talk) * 0.3;
    const { pucker, length, twist, tilt, lift, droop, ripple } = s.pose;
    const shake = s.shiver(now, s.lump);
    const centre: [number, number] = [
      CX + shake,
      CY - lift + 1.2 * Math.sin(t * 2.2),
    ];
    // Talking: it speaks through the middle.
    const inner = 3 + 9 * pucker + s.talk * 5 * syllable(t);
    set(group.current, {
      transform: `rotate(${(tilt + s.lean).toFixed(2)} ${centre[0].toFixed(2)} ${centre[1].toFixed(2)})`,
    });
    for (let i = 0; i < SWIRL_PETALS; i++) {
      const pulse = ripple * 0.35 * travelling(t, i, 6, 0.9);
      set(petals.current[i] ?? null, {
        d: swirlPetal(
          centre,
          s.angle + (i / SWIRL_PETALS) * Math.PI * 2,
          inner,
          length * (1 + pulse),
          twist,
          droop,
        ),
      });
    }
    set(hole.current, {
      cx: centre[0],
      cy: centre[1],
      rx: inner * 0.9,
      ry: inner * 0.9 * (1 - 0.25 * droop),
    });
    set(shadow.current, { rx: 24 - Math.max(0, lift) * 0.8 });
    drawExtras(
      extras,
      mood,
      { t, dt },
      {
        face: centre,
        top: [centre[0], centre[1] - inner - length * 0.85],
        ground: CY + 33,
      },
    );
  });

  return (
    <Stage ref={stage}>
      <ellipse ref={shadow} cx={CX} cy={CY + 34} rx={24} ry={3} fill={SHADOW} />
      <ExtrasBehind extras={extras} />
      <g ref={group}>
        {Array.from({ length: SWIRL_PETALS }, (_, i) => (
          <path
            key={`p${i}`}
            ref={(el) => {
              petals.current[i] = el;
            }}
            fill={theme.body}
          />
        ))}
        <ellipse ref={hole} fill={theme.shade} />
      </g>
      <ExtrasFront extras={extras} />
    </Stage>
  );
};

/** All helpers, in the order they were first shown. */
export const AI_CHARACTERS: {
  id: string;
  name: string;
  description: string;
  Component: AiCharacterComponent;
}[] = [
  {
    id: 'blob',
    name: 'Blob',
    description:
      'The soft blob with the flat eyes, now with body animation: hops with a crouch and a landing, leans, droops, and springs when it starts talking.',
    Component: AiBlob,
  },
  {
    id: 'morph',
    name: 'Morph',
    description:
      'The body morphs between strict shapes (circle, squircle, triangle, diamond, star, drop). Flat eyes, no mouth.',
    Component: AiMorph,
  },
  {
    id: 'eyes',
    name: 'Eyes',
    description:
      'No body: two floating flat eyes with thin brows. Talking squashes the eyes in rhythm.',
    Component: AiEyes,
  },
  {
    id: 'capsule',
    name: 'Capsule',
    description:
      'A flat capsule that speaks with body language: leans, stretches tall, hops, slumps. Scanning, it spins like a barrel: the eyes slide round and come back from the other side.',
    Component: AiCapsule,
  },
  {
    id: 'logo',
    name: 'Logo',
    description:
      'The Nodon N with no face: seven nodes and four strokes. Every mood is a shape and a motion: it breathes, stretches, tilts, sways, jumps, reads along a line, circles, builds a wall or slumps into a pile; the strokes fade when the nodes leave the N.',
    Component: AiLogo,
  },
  {
    id: 'swirl',
    name: 'Swirl',
    description:
      'Every AI company logo at once: a rosette of curled petals around a puckered middle, no face. It spins as a loader, clenches when it thinks or is proud, opens wide when curious or excited, wilts when sad and talks through the middle.',
    Component: AiSwirl,
  },
];
